import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Navbar } from "./components/landing/Navbar";
import { Hero } from "./components/landing/Hero";
import { Problem } from "./components/landing/Problem";
import { Features } from "./components/landing/Features";
import { HowItWorks } from "./components/landing/HowItWorks";
import { Benefits } from "./components/landing/Benefits";
import { CTA } from "./components/landing/CTA";
import { Footer } from "./components/landing/Footer";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { WhiteboardPage } from "./pages/WhiteboardPage";
import { SharedWhiteboardPage } from "./pages/SharedWhiteboardPage";
import { MessagesPage } from "./pages/MessagesPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { KanbanBoardPage } from "./pages/KanbanBoardPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import MeetingsPage from "./pages/MeetingsPage";
import MeetingRoom from "./pages/MeetingRoom";
import GroupChatPage from "./pages/GroupChatPage";
import JoinPage from "./pages/JoinPage";
import DMChatPage from "./pages/DMChatPage";
import OnboardingPage from "./pages/OnboardingPage";
import AdminPage from "./pages/AdminPage";
import { AppSidebar } from "./components/layout/AppSidebar";
import { Toaster } from "./components/ui/sonner";
import { Menu } from "lucide-react";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <Hero />
        <Problem />
        <Features />
        <HowItWorks />
        <Benefits />
        <CTA />
      </main>
      <Footer />
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-[#64748B]">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check onboarding completion - allow access to onboarding page and admin page
  const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true';
  if (!onboardingComplete && location.pathname !== '/onboarding' && location.pathname !== '/admin') {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-[#64748B]">Loading...</div>
      </div>
    );
  }
  
  // If authenticated, check onboarding status before redirecting
  if (isAuthenticated) {
    const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true';
    
    // If onboarding is not complete, redirect to onboarding
    if (!onboardingComplete) {
      if (location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
      }
      // Allow access to onboarding page even if authenticated
      return children;
    }
    
    // If onboarding is complete and user is on public routes (login/register), redirect to dashboard
    if (onboardingComplete && (location.pathname === '/login' || location.pathname === '/register')) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

// Layout wrapper for authenticated pages with persistent sidebar
const AuthenticatedLayout = ({ children, selectedChannel, onSelectChannel }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1d21] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check onboarding completion - allow access to onboarding page and admin page
  const onboardingComplete = localStorage.getItem('onboardingComplete') === 'true';
  if (!onboardingComplete && location.pathname !== '/onboarding' && location.pathname !== '/admin') {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="min-h-screen bg-[#1a1d21]">
      {/* Mobile overlay when sidebar open */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          aria-hidden
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <AppSidebar
        selectedChannel={selectedChannel}
        onSelectChannel={onSelectChannel}
        mobileOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
      />
      {/* Mobile top bar with menu button */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#1a1d21] border-b border-white/10 z-20 flex items-center px-4">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>
      <main className="pt-14 md:pt-0 ml-0 md:ml-[344px] min-h-screen">
        {children}
      </main>
    </div>
  );
};

function AppRoutes() {
  const [selectedChannel, setSelectedChannel] = useState(null);
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/dashboard" element={
        <AuthenticatedLayout selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel}>
          <DashboardPage />
        </AuthenticatedLayout>
      } />
      <Route path="/whiteboard" element={
        <ProtectedRoute>
          <WhiteboardPage />
        </ProtectedRoute>
      } />
      <Route path="/whiteboard/:id" element={
        <ProtectedRoute>
          <WhiteboardPage />
        </ProtectedRoute>
      } />
      <Route path="/whiteboard/shared/:shareToken" element={<SharedWhiteboardPage />} />
      <Route path="/messages" element={
        <AuthenticatedLayout selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel}>
          <MessagesPage selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel} />
        </AuthenticatedLayout>
      } />
      <Route path="/projects" element={
        <AuthenticatedLayout selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel}>
          <ProjectsPage />
        </AuthenticatedLayout>
      } />
      <Route path="/projects/:projectId" element={
        <AuthenticatedLayout selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel}>
          <KanbanBoardPage />
        </AuthenticatedLayout>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/meetings" element={
        <AuthenticatedLayout selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel}>
          <MeetingsPage />
        </AuthenticatedLayout>
      } />
      <Route path="/meeting/:meetingId" element={
        <ProtectedRoute>
          <MeetingRoom />
        </ProtectedRoute>
      } />
      <Route path="/group/:groupId" element={
        <ProtectedRoute>
          <AuthenticatedLayout selectedChannel={null} onSelectChannel={() => {}}>
            <GroupChatPage />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/dm/:conversationId" element={
        <ProtectedRoute>
          <AuthenticatedLayout selectedChannel={null} onSelectChannel={() => {}}>
            <DMChatPage />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AuthenticatedLayout selectedChannel={selectedChannel} onSelectChannel={setSelectedChannel}>
          <AdminPage />
        </AuthenticatedLayout>
      } />
    </Routes>
  );
}

function App() {
  // Filter out browser extension errors from console
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args) => {
      const errorString = args.join(' ');
      // Filter out common browser extension errors
      if (
        errorString.includes('chrome-extension://') ||
        errorString.includes('sseError') ||
        errorString.includes('Extension context invalidated') ||
        errorString.includes('chrome.runtime') ||
        errorString.includes('moz-extension://') ||
        errorString.includes('safari-extension://')
      ) {
        // Silently ignore extension errors
        return;
      }
      originalError.apply(console, args);
    };
    
    console.warn = (...args) => {
      const warnString = args.join(' ');
      // Filter out common browser extension warnings
      if (
        warnString.includes('chrome-extension://') ||
        warnString.includes('moz-extension://') ||
        warnString.includes('safari-extension://')
      ) {
        return;
      }
      originalWarn.apply(console, args);
    };
    
    // Global error handler to catch unhandled errors from extensions
    const handleError = (event) => {
      const error = event.error || event.message || '';
      const errorString = String(error);
      
      // Filter out extension errors
      if (
        errorString.includes('chrome-extension://') ||
        errorString.includes('sseError') ||
        errorString.includes('Extension context') ||
        event.filename?.includes('chrome-extension://') ||
        event.filename?.includes('moz-extension://')
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    
    window.addEventListener('error', handleError, true);
    
    // Cleanup
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError, true);
    };
  }, []);
  
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="bottom-right" />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
